import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  PoButtonModule,
  PoFieldModule,
  PoIconModule,
  PoNotificationService,
  PoPageModule,
  PoToasterOrientation,
} from '@po-ui/ng-components';
import {
  HotJarService,
  JobOpportunityListItem,
  JobOpportunityListStateService,
  LocalitiesService,
} from '@services/index';
import { SupportButtonService } from '@shared/support-button/services/support-button.service';
import { TipAccordionComponent } from '@shared/tip-accordion/tip-accordion.component';
import {
  AdvancedFilter,
  AtsNumberedPaginationComponent,
  AtsUiLibModule,
  FormUtils,
  HiringRegimeTypeEnum,
  LoaderService,
  ThemeStateService,
  ThemeType,
} from 'ats-ui-lib';
import { combineLatest, lastValueFrom, map, Subject } from 'rxjs';
import { NoStandaloneSharedModule } from '../../no-standalone-shared/no-standalone-shared.module';
import { JobOpportunityCardComponent } from './pages/job-opportunity-card/job-opportunity-card.component';

@Component({
  selector: 'ats-portal',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TipAccordionComponent,
    AtsUiLibModule,
    PoFieldModule,
    PoButtonModule,
    PoIconModule,
    PoPageModule,
    JobOpportunityCardComponent,
    NoStandaloneSharedModule,
  ],
  providers: [CurrencyPipe],
  templateUrl: './portal.component.html',
  styleUrl: './portal.component.css',
})
export class PortalComponent implements OnInit, AfterViewInit, OnDestroy {
  public state$ = this.stateService.state$;

  public stateSignal = toSignal(this.stateService.state$);

  public publicationPeriodOptions$ = combineLatest([
    this.translateService.stream([
      'l-published-at-one-day',
      'l-published-at-three-days',
      'l-published-at-one-week',
      'l-published-at-one-month',
      'l-published-more-than-a-month',
    ]),
  ]).pipe(
    map(([translations]) => {
      return [
        { value: 1, label: translations['l-published-at-one-day'] },
        { value: 3, label: translations['l-published-at-three-days'] },
        { value: 7, label: translations['l-published-at-one-week'] },
        { value: 30, label: translations['l-published-at-one-month'] },
        { value: 31, label: translations['l-published-more-than-a-month'] },
      ];
    }),
  );

  readonly hiringRegimeOptions$ = this.translateService
    .stream([
      'l-internship',
      'l-freelancer',
      'l-cooperated',
      'l-self-employed',
      'l-scholarship-holder',
      'l-apprentice',
      'l-temp',
      'l-volunteer',
    ])
    .pipe(
      map((translations) => [
        {
          label: 'CLT',
          value: HiringRegimeTypeEnum.CLT,
        },
        {
          label: 'PJ',
          value: HiringRegimeTypeEnum.PJ,
        },
        {
          label: translations['l-internship'],
          value: HiringRegimeTypeEnum.Internship,
        },
        {
          label: translations['l-freelancer'],
          value: HiringRegimeTypeEnum.Freelancer,
        },
        {
          label: translations['l-cooperated'],
          value: HiringRegimeTypeEnum.Cooperated,
        },
        {
          label: translations['l-self-employed'],
          value: HiringRegimeTypeEnum.SelfEmployed,
        },
        {
          label: translations['l-scholarship-holder'],
          value: HiringRegimeTypeEnum.ScholarshipHolder,
        },
        {
          label: translations['l-apprentice'],
          value: HiringRegimeTypeEnum.Apprentice,
        },
        {
          label: translations['l-temp'],
          value: HiringRegimeTypeEnum.Temp,
        },
        {
          label: translations['l-volunteer'],
          value: HiringRegimeTypeEnum.Volunteer,
        },
      ]),
    );

  public formGroup = this.fb.group({
    filter: [''],
    hiringRegime: [],
    isLocal: [false],
    isHybrid: [false],
    isHomeOffice: [false],
    publicationRange: [null],
    careerPageId: [null],
    stateId: [''],
    cityId: [''],
    cityName: [''],
    stateSmallName: [''],
    isJobApplicationSimplified: [false],
  });

  public advancedFilterChangeSubject = new Subject<AdvancedFilter | undefined>();

  trackBy = (_index: number, item: JobOpportunityListItem): string | undefined => item.id;

  readonly comboParams = { pageSize: 50, order: 'name' };

  public stateUrl = `${environment.applicantApiEndpoint}/localities/states/simple`;

  public cityUrl = `${environment.applicantApiEndpoint}/localities/cities/simple`;

  public companyUrl = `${environment.jobPortalUrl}/api/career-page/by-divulgation-name`;

  protected readonly jobPortalUrl = environment.jobPortalUrl;

  protected readonly helpUrl = 'https://forms.gle/961JWnCqzdR8L9y8A';

  protected readonly isSearchMeaningful = signal(false);

  protected readonly termsUseCurrentVersion = 1;

  private randomCharAnnouncer = '';

  private pagination = viewChild(AtsNumberedPaginationComponent);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly loaderService: LoaderService,
    private readonly translateService: TranslateService,
    private readonly localitiesService: LocalitiesService,
    private readonly supportButtonService: SupportButtonService,
    private readonly stateService: JobOpportunityListStateService,
    private readonly announcer: LiveAnnouncer,
    private readonly themeStateService: ThemeStateService,
    private readonly window: Window,
    private readonly hotJarService: HotJarService,
    private readonly notificationService: PoNotificationService,
  ) {
    this.advancedFilterChangeSubject.subscribe(this.onChangeFilter.bind(this));
  }

  private goToTop(): void {
    this.window.scrollTo({ top: 0, behavior: 'instant' });
  }

  public async ngOnInit(): Promise<void> {
    this.themeStateService.setCurrentTheme(ThemeType.Trampo);
    this.supportButtonService.hide();

    this.loaderService.show();
    try {
      await this.handleInitialSearch();
    } finally {
      this.loaderService.hide();
    }
  }

  private async ifPageIsEmptyRedirectToFirstPage(): Promise<void> {
    const snapshot = this.stateService.snapshot;
    if (snapshot.initialized && snapshot.isEmpty && snapshot.pageNumber > 1) {
      this.pagination()!.onFirstClick();
      this.notifyIndexPageNotFound();
    }
  }

  private notifyIndexPageNotFound(): void {
    this.notificationService.warning({
      orientation: PoToasterOrientation.Top,
      message: this.translateService.instant('l-index-page-not-found'),
      duration: 9000,
    });
  }

  public ngAfterViewInit(): void {
    const { cityId, stateId, isHomeOffice, isHybrid, isLocal, hiringRegime } = this.formGroup.value;

    const filter = {
      ...this.stateService.snapshot.advancedFilter,
      cityId,
      stateId,
      hiringRegime,
      isHomeOffice: !!isHomeOffice,
      isHybrid: !!isHybrid,
      isLocal: !!isLocal,
    };

    this.advancedFilterChangeSubject.next(filter);

    this.hotJarService.tryTriggerRecord();
  }

  public ngOnDestroy(): void {
    this.themeStateService.setCurrentTheme(ThemeType.Default);
    this.supportButtonService.show();
  }

  public async onChangeCity(cityId?: string): Promise<void> {
    if (cityId) {
      let result = await lastValueFrom(this.localitiesService.getCity(cityId));

      if (result.state.smallName) {
        FormUtils.getFormControl(this.formGroup, 'cityName').setValue(result.name);
        FormUtils.getFormControl(this.formGroup, 'stateSmallName').setValue(result.state.smallName);
      }
    } else {
      FormUtils.getFormControl(this.formGroup, 'cityName').setValue('');
      const currentStateId = FormUtils.getFormControl(this.formGroup, 'stateId').value;
      if (!currentStateId) {
        FormUtils.getFormControl(this.formGroup, 'stateSmallName').setValue('');
      }
    }
  }

  public async onChangeState(stateId?: string): Promise<void> {
    FormUtils.getFormControl(this.formGroup, 'cityId').setValue('');
    FormUtils.getFormControl(this.formGroup, 'cityName').setValue('');

    this.setCityUrl();

    if (!!stateId) {
      let result = await lastValueFrom(this.localitiesService.getState(stateId));

      if (result.smallName) {
        FormUtils.getFormControl(this.formGroup, 'stateSmallName').setValue(result.smallName);
      }
    } else {
      FormUtils.getFormControl(this.formGroup, 'stateSmallName').setValue('');
    }
  }

  async filter(): Promise<void> {
    const body = this.getValidFormValue();

    this.loaderService.show();

    try {
      if (!this.stateService.snapshot.isLoading) {
        await this.stateService.setFilterAndAdvancedFilter(body.filter ?? '', {
          ...body,
        });
      }
    } finally {
      this.loaderService.hide();
    }

    await this.announceSearchResult();
  }

  public clear(): void {
    this.formGroup.reset();

    ['isHomeOffice', 'isHybrid', 'isLocal', 'isJobApplicationSimplified'].forEach((control) => {
      FormUtils.getFormControl(this.formGroup, control).setValue(false);
    });
  }

  public async announceClearSearch(): Promise<void> {
    await this.announcer.announce(
      this.translateService.instant('l-advanced-filter-successfully-cleaned'),
      'assertive',
    );
  }

  public async onSearch(): Promise<void> {
    this.loaderService.show();

    try {
      if (!this.stateService.snapshot.isLoading) {
        const searchValue = FormUtils.getFormControl(this.formGroup, 'filter').value;
        await this.stateService.setFilterAndAdvancedFilter(searchValue, this.getValidFormValue());

        this.isSearchMeaningful.set(searchValue && searchValue.length > 0);
      }
    } finally {
      this.loaderService.hide();
    }

    await this.announceSearchResult();
  }

  private async announceSearchResult(): Promise<void> {
    const message =
      this.stateService.snapshot.page.length <= 0
        ? this.translateService.instant('l-no-job-opportunities-found')
        : this.translateService.instant('l-job-opportunities-found');

    this.randomCharAnnouncer = this.randomCharAnnouncer === '\u200B' ? '\u200C' : '\u200B';

    await this.announcer.announce(`${message} ${this.randomCharAnnouncer}`, 'polite');
  }

  private async onChangeFilter(advancedFilter: AdvancedFilter | undefined): Promise<void> {
    if (advancedFilter) {
      this.clear();
      this.formGroup.patchValue(advancedFilter);
    } else this.clear();
  }

  private async handleInitialSearch(): Promise<void> {
    const { search: queryParamsSearchFilter } = this.activatedRoute.snapshot.queryParams;

    if (queryParamsSearchFilter) {
      FormUtils.getFormControl(this.formGroup, 'filter').setValue(queryParamsSearchFilter);
      this.stateService.fillFilterAndAdvancedFilter(
        queryParamsSearchFilter,
        this.getValidFormValue(),
      );
    }
    await this.stateService.init();
  }

  private getValidFormValue(): any {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cityId, stateId, ...formValue } = this.formGroup.getRawValue();

    const transformedFormValue = { ...formValue };
    (['isHomeOffice', 'isHybrid', 'isLocal'] as const).forEach((control) => {
      if (transformedFormValue[control] === false) {
        transformedFormValue[control] = null;
      }
    });

    return transformedFormValue;
  }

  private setCityUrl(): void {
    const stateId = FormUtils.getFormControl(this.formGroup, 'stateId').value;

    this.cityUrl = !!stateId
      ? `${environment.applicantApiEndpoint}/localities/state/${stateId}/cities/simple`
      : `${environment.applicantApiEndpoint}/localities/cities/simple`;
  }

  public async onPageChange(page: number): Promise<void> {
    const currentPage = this.stateService.snapshot.pageNumber;

    if (page === currentPage || this.stateService.snapshot.isLoading) {
      return;
    }

    this.goToTop();

    this.loaderService.show();

    try {
      await this.stateService.goToPage(page);
      const message = this.translateService.instant('l-current-page', { page });
      await this.announcer.announce(message, 'polite');
    } finally {
      this.loaderService.hide();
    }
    await this.ifPageIsEmptyRedirectToFirstPage();
  }

  public trackByItemId(index: number, item: any): string {
    return item?.id || index.toString();
  }
}
